import { useState, useCallback } from "react";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { MapPicker } from "./MapPicker";
import { Button } from "@/components/ui/button";
import { MapPin, Map, Locate, Loader2 } from "lucide-react";
import { getCurrentLocation, reverseGeocode, type GeocodingResult } from "@/services/geocoding";
import { cn } from "@/lib/utils";

export interface LocationData {
  lat: number;
  lon: number;
  address?: GeocodingResult | null;
  formattedAddress?: string;
}

interface LocationPickerProps {
  value?: LocationData | null;
  onChange?: (location: LocationData | null) => void;
  placeholder?: string;
  countryCode?: string;
  className?: string;
  showMap?: boolean;
  mapHeight?: string;
  disabled?: boolean;
}

export function LocationPicker({
  value,
  onChange,
  placeholder = "Digite seu endereço ou selecione no mapa",
  countryCode = "br",
  className,
  showMap = true,
  mapHeight = "300px",
  disabled = false,
}: LocationPickerProps) {
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lon: number } | null>(
    value ? { lat: value.lat, lon: value.lon } : null
  );

  // Handle address selection from autocomplete
  const handleAddressSelect = useCallback(
    (result: GeocodingResult | null) => {
      if (result) {
        const location: LocationData = {
          lat: result.lat,
          lon: result.lon,
          address: result,
          formattedAddress: result.displayName,
        };
        setSelectedLocation({ lat: result.lat, lon: result.lon });
        onChange?.(location);
      } else {
        setSelectedLocation(null);
        onChange?.(null);
      }
    },
    [onChange]
  );

  // Handle map selection
  const handleMapSelect = useCallback(
    (coords: { lat: number; lon: number }, address?: GeocodingResult | null) => {
      const location: LocationData = {
        lat: coords.lat,
        lon: coords.lon,
        address: address,
        formattedAddress: address?.displayName,
      };
      setSelectedLocation(coords);
      onChange?.(location);
    },
    [onChange]
  );

  // Handle "Use my location" button
  const handleUseMyLocation = async () => {
    setIsLocating(true);
    try {
      const coords = await getCurrentLocation();
      const address = await reverseGeocode(coords.lat, coords.lon);
      
      const location: LocationData = {
        lat: coords.lat,
        lon: coords.lon,
        address: address,
        formattedAddress: address?.displayName,
      };
      
      setSelectedLocation(coords);
      onChange?.(location);
      
      // Expand map to show the location
      if (showMap) {
        setIsMapExpanded(true);
      }
    } catch (error) {
      console.error("Location error:", error);
      alert(error instanceof Error ? error.message : "Erro ao obter localização");
    } finally {
      setIsLocating(false);
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Address input row */}
      <div className="flex gap-2">
        <div className="flex-1">
          <AddressAutocomplete
            value={value?.address}
            onChange={handleAddressSelect}
            placeholder={placeholder}
            countryCode={countryCode}
            disabled={disabled}
          />
        </div>
        
        {/* Use my location button */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleUseMyLocation}
          disabled={disabled || isLocating}
          title="Usar minha localização"
          className="flex-shrink-0"
        >
          {isLocating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Locate className="w-4 h-4" />
          )}
        </Button>

        {/* Toggle map button */}
        {showMap && (
          <Button
            type="button"
            variant={isMapExpanded ? "default" : "outline"}
            size="icon"
            onClick={() => setIsMapExpanded(!isMapExpanded)}
            disabled={disabled}
            title={isMapExpanded ? "Esconder mapa" : "Mostrar mapa"}
            className={cn(
              "flex-shrink-0",
              isMapExpanded && "bg-orange-500 hover:bg-orange-600"
            )}
          >
            <Map className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Selected location preview */}
      {value && (
        <div className="flex items-start gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
          <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">
              {value.formattedAddress || `${value.lat.toFixed(6)}, ${value.lon.toFixed(6)}`}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Coordenadas: {value.lat.toFixed(6)}, {value.lon.toFixed(6)}
            </p>
          </div>
        </div>
      )}

      {/* Map */}
      {showMap && isMapExpanded && (
        <div 
          className="rounded-lg overflow-hidden border border-gray-200"
          style={{ height: mapHeight }}
        >
          <MapPicker
            selectedLocation={selectedLocation}
            onLocationSelect={handleMapSelect}
            className="w-full h-full"
            showLocateButton={true}
            reverseGeocodeOnSelect={true}
          />
        </div>
      )}
    </div>
  );
}

// Also export the sub-components for flexibility
export { AddressAutocomplete } from "./AddressAutocomplete";
export { MapPicker } from "./MapPicker";

